import { ApolloClient, gql } from '@apollo/client';

export const GET_LISTING_SLUGS = gql`
  query GetListingPageSlugs(
    $tag: String!
    $locale: String!
    $preview: Boolean
  ) {
    libraryMeetingPlaceCollection(
      where: { contentfulMetadata: { tags: { id_contains_some: [$tag] } } }
      locale: $locale
      preview: $preview
      limit: 2
    ) {
      total
      items {
        sys {
          id
        }
        eventListPage {
          sys {
            id
          }
          slug
        }
        offerListPage {
          sys {
            id
          }
          slug
        }
        occationListPage {
          sys {
            id
          }
          slug
        }
      }
    }
    meetingPlacePageHomeCollection(
      where: { contentfulMetadata: { tags: { id_contains_some: [$tag] } } }
      locale: $locale
      preview: $preview
      limit: 2
    ) {
      total
      items {
        meetingPlace {
          ... on LibraryMeetingPlace {
            sys {
              id
            }
          }
        }
        menuItemCollection(limit: 20) {
          total
          items {
            ... on LibraryMenuGroup {
              pagesCollection(limit: 50) {
                total
                items {
                  ... on MeetingPlacePageEventList {
                    sys {
                      id
                    }
                  }
                  ... on MeetingPlacePageOfferList {
                    sys {
                      id
                    }
                  }
                  ... on MeetingPlacePageOccasions {
                    sys {
                      id
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`;

export interface ListingSlugs {
  eventListPageSlug: string | null;
  offerListPageSlug: string | null;
}

interface EntryReference {
  sys?: { id: string } | null;
}

interface ListingPage extends EntryReference {
  slug?: string | null;
}

interface EntryCollection<Item> {
  total: number;
  items: (Item | null)[];
}

interface ListingSlugsQueryData {
  libraryMeetingPlaceCollection?: EntryCollection<
    EntryReference & {
      eventListPage?: ListingPage | null;
      offerListPage?: ListingPage | null;
      occationListPage?: ListingPage | null;
    }
  > | null;
  meetingPlacePageHomeCollection?: EntryCollection<{
    meetingPlace?: EntryReference | null;
    menuItemCollection?: EntryCollection<{
      pagesCollection?: EntryCollection<EntryReference> | null;
    }> | null;
  }> | null;
}

/**
 * Fetches canonical listing slugs that appear in the meeting place's homepage menu.
 * Dedicated lists take priority over the canonical occasions list.
 *
 * @example
 * // Get all slugs
 * const { eventListPageSlug, offerListPageSlug } =
 *   await getListingPageSlugs(client, tag, locale);
 *
 * // Get event slug (falls back to occasions if eventList not found) as for offers
 * const { eventListPageSlug } = await getListingPageSlugs(client, tag, locale);
 */
export const getListingPageSlugs = async (
  client: ApolloClient,
  tag: string,
  locale: string,
  preview?: boolean,
): Promise<ListingSlugs> => {
  try {
    const result = await client.query<ListingSlugsQueryData>({
      query: GET_LISTING_SLUGS,
      variables: { tag, locale, preview: preview ?? false },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });

    const meetingPlaces = result.data?.libraryMeetingPlaceCollection;
    const homepages = result.data?.meetingPlacePageHomeCollection;
    const meetingPlace = meetingPlaces?.items[0];
    const homepage = homepages?.items[0];
    const menu = homepage?.menuItemCollection;

    if (
      meetingPlaces?.total !== 1 ||
      homepages?.total !== 1 ||
      !meetingPlace?.sys?.id ||
      homepage?.meetingPlace?.sys?.id !== meetingPlace.sys.id ||
      !menu ||
      menu.total !== menu.items.length
    ) {
      return { eventListPageSlug: null, offerListPageSlug: null };
    }

    const menuPageIds = new Set<string>();
    for (const group of menu.items) {
      const pages = group?.pagesCollection;
      if (!pages) continue;
      if (pages.total !== pages.items.length) {
        return { eventListPageSlug: null, offerListPageSlug: null };
      }
      for (const page of pages.items) {
        if (page?.sys?.id) menuPageIds.add(page.sys.id);
      }
    }

    const getMenuSlug = (page?: ListingPage | null): string | null =>
      page?.sys?.id && menuPageIds.has(page.sys.id) && page.slug?.trim()
        ? page.slug
        : null;
    const occasionsSlug = getMenuSlug(meetingPlace.occationListPage);

    return {
      eventListPageSlug:
        getMenuSlug(meetingPlace.eventListPage) ?? occasionsSlug,
      offerListPageSlug:
        getMenuSlug(meetingPlace.offerListPage) ?? occasionsSlug,
    };
  } catch (error) {
    console.error('[getListingPageSlugs] Error:', error);
    return {
      eventListPageSlug: null,
      offerListPageSlug: null,
    };
  }
};

